import * as React from 'react';
import type { ProductCategoryMappingV2, ProductCategoryMappingV2Sql } from '@gcv/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GcvAdminApi } from 'api';
import { PRODUCT_CATEGORY_MAP_V2_KEY } from 'tanstack-hooks/keys';
import { chunkArray } from 'util/array.util';

/** Rows per upsert request. */
export const PRODUCT_CATEGORY_MAP_V2_UPSERT_BATCH_SIZE = 100;

export function useUpsertProductCategoryMapV2Rows() {
  const gcvAdminApi = new GcvAdminApi();
  const queryClient = useQueryClient();

  // rows written so far, for caller progress display
  const [upsertedRowCount, setUpsertedRowCount] = React.useState(0);

  const { mutateAsync: upsertProductCategoryMapV2Rows, isPending: isUpsertingProductCategoryMapV2Rows } =
    useMutation({
      mutationKey: [PRODUCT_CATEGORY_MAP_V2_KEY, 'upsert'],
      mutationFn: async (rows: ProductCategoryMappingV2[]) => {
        setUpsertedRowCount(0);

        const upsertedRows: ProductCategoryMappingV2Sql[] = [];
        let requested = 0;
        let upserted = 0;

        // sequential: keeps the load steady and stops the run on the first failure
        for (const batch of chunkArray(rows, PRODUCT_CATEGORY_MAP_V2_UPSERT_BATCH_SIZE)) {
          const result = await gcvAdminApi.upsertProductCategoryMapV2Rows(batch);
          requested += result.requested;
          upserted += result.upserted;
          upsertedRows.push(...result.rows);
          setUpsertedRowCount(upsertedRows.length);
        }

        return { requested, upserted, rows: upsertedRows };
      },
      // onSettled: a run that fails partway has still written its earlier batches
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: [PRODUCT_CATEGORY_MAP_V2_KEY] });
      },
      meta: {
        snackbarSuccessMessage: 'Product category mapping saved successfully.',
        snackbarErrorMessage:
          'There was an issue saving the product category mapping. Please try again, or contact support if the problem persists.'
      }
    });

  return { upsertProductCategoryMapV2Rows, isUpsertingProductCategoryMapV2Rows, upsertedRowCount };
}
